import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import toast from 'react-hot-toast';

interface StarRatingProps {
  rating: number;
  ratingCount?: number;
  onRate?: (rating: number) => void;
  size?: number;
  readOnly?: boolean;
}

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  ratingCount,
  onRate,
  size = 16,
  readOnly = false
}) => {
  const [displayRating, setDisplayRating] = useState(rating);
  const [currentRatingCount, setCurrentRatingCount] = useState(ratingCount);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    setDisplayRating(rating);
    setCurrentRatingCount(ratingCount);
  }, [rating, ratingCount]);

  const handleRating = (e: React.MouseEvent, rate: number) => {
    e.stopPropagation();
    if (readOnly) {
      toast.error('Please login to vote');
      return;
    }
    if (!onRate) return;
    setDisplayRating(rate);
    onRate(rate);
  };

  const handleMouseEnter = (rate: number) => {
    if (readOnly) return;
    setHoverRating(rate);
  };

  const handleMouseLeave = () => {
    if (readOnly) return;
    setHoverRating(0);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center" onMouseLeave={handleMouseLeave}>
        {Array.from({ length: 5 }, (_, i) => {
          const starValue = i + 1;
          const currentRating = hoverRating > 0 ? hoverRating : displayRating;
          const isFilled = starValue <= currentRating;

          return (
            <Star
              key={starValue}
              size={size}
              className={`cursor-pointer transition-colors duration-200 ${isFilled ? 'text-yellow-400' : 'text-gray-500'}`}
              fill={isFilled ? 'currentColor' : 'none'}
              onClick={(e) => handleRating(e, starValue)}
              onMouseEnter={() => handleMouseEnter(starValue)}
            />
          );
        })}
      </div>
      {currentRatingCount !== undefined && (
        <span className="text-xs text-gray-400">({currentRatingCount})</span>
      )}
    </div>
  );
};
